import React from "react";
import { InfiniteLoader, AutoSizer, Table, Column } from "react-virtualized";
import { useTable, useGroupBy, useExpanded, useFlexLayout, useSortBy, Column as ReactTableColumn } from "react-table";
import { Icon } from "semantic-ui-react";
import OverflowTextTooltip from "./global_components/OverflowTextTooltip";

import "react-virtualized/styles.css"; // only needs to be imported once
import "./VZenTable.scss";

interface IVZenTableProps<ColumnType extends Object = {}> {
  columns: ReactTableColumn<ColumnType>[];
  data: any;
  initialState?: any;
  containerProps?: any;
  infiniteLoaderProps?: any;
  tableProps?: any;
  useTableProps?: any;
  sortBy?: string;
  sortDirection?: string;
}

export const VZenTable = <ColumnType extends Object = {}>({
  columns,
  data,
  sortBy,
  sortDirection,
  containerProps,
  infiniteLoaderProps,
  tableProps,
  useTableProps,
}: IVZenTableProps<ColumnType>) => {
  const { headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data,
      ...useTableProps,
    },
    useGroupBy,
    useSortBy,
    useExpanded,
    useFlexLayout,
  );

  return (
    <div className='VZenTable' {...containerProps}>
      <InfiniteLoader {...infiniteLoaderProps}>
        {({ onRowsRendered, registerChild }) => (
          <AutoSizer defaultWidth={1000} defaultHeight={800}>
            {({ width, height }) => (
              <Table
                {...tableProps}
                ref={registerChild}
                onRowsRendered={(data) => {
                  onRowsRendered(data);
                  if (tableProps.onRowsRendered) {
                    tableProps.onRowsRendered(data);
                  }
                }}
                overscanRowCount={25}
                rowGetter={({ index }) => {
                  const row = rows[index];
                  prepareRow(row);
                  return row;
                }}
                rowCount={rows.length}
                height={height}
                width={width}
              >
                {headerGroups[0]?.headers.map((column: any, id: number) => (
                  <Column
                    key={`${column.id}-${id}`}
                    dataKey={column.id}
                    width={parseInt(column.width)}
                    flexGrow={column.flexGrow}
                    flexShrink={column.flexShrink}
                    style={column.style}
                    headerRenderer={() => {
                      // If the header id is a toggle, do not render a tooltip
                      if (column.id === "toggle" || column.id === "scroll_to_top") {
                        return <div>{column.render("Header")}</div>;
                      }
                      return (
                        <OverflowTextTooltip
                          content={
                            <div
                              role='button'
                              className='ellipses-overflow'
                              tabIndex={-1}
                              onClick={() => {
                                let nextSort =
                                  column.isSortedDesc === undefined ? column.sortDescFirst : !column.isSortedDesc;
                                if (column.id === "expired_at") {
                                  nextSort = sortBy === "expired_at" && sortDirection === "desc" ? false : true;
                                  tableProps?.onSortChanged("expired_at", nextSort);
                                } else if (column.id === "created_at") {
                                  nextSort = sortBy === "submitted_at" && sortDirection === "desc" ? false : true;
                                  tableProps?.onSortChanged("submitted_at", nextSort);
                                } else if (column.id === "creator_id") {
                                  nextSort =
                                    sortBy === "creator.full_name.keyword" && sortDirection === "desc" ? false : true;
                                  tableProps?.onSortChanged("creator.full_name.keyword", nextSort);
                                } else if (column.id === "balance_due") {
                                  nextSort = sortBy === "total_pending" && sortDirection === "desc" ? false : true;
                                  tableProps?.onSortChanged("total_pending", nextSort);
                                } else if (column.id === "dateLastUpdated") {
                                  nextSort = sortBy === "dateLastUpdated" && sortDirection === "desc" ? false : true;
                                  tableProps?.onSortChanged("dateLastUpdated", nextSort);
                                } else if (column.id === "dateEntered") {
                                  nextSort = sortBy === "dateEntered" && sortDirection === "desc" ? false : true;
                                  tableProps?.onSortChanged("dateEntered", nextSort);
                                } else if (column.id === "dateDue") {
                                  nextSort = sortBy === "dateDue" && sortDirection === "desc" ? false : true;
                                  tableProps?.onSortChanged("dateDue", nextSort);
                                } else if (column.id === "dateExpectClose") {
                                  nextSort = sortBy === "dateExpectClose" && sortDirection === "desc" ? false : true;
                                  tableProps?.onSortChanged("dateExpectClose", nextSort);
                                } else if (column.id === "reservation_number") {
                                  nextSort =
                                    sortBy === "reservation_number.keyword" && sortDirection === "desc" ? false : true;
                                  tableProps?.onSortChanged("reservation_number.keyword", nextSort);
                                } else if (column.id === "confirmation_number") {
                                  nextSort =
                                    sortBy === "confirmation_number.keyword" && sortDirection === "desc" ? false : true;
                                  tableProps?.onSortChanged("confirmation_number.keyword", nextSort);
                                } else if (column.id === "status") {
                                  nextSort = sortBy === "status" && sortDirection === "desc" ? false : true;
                                  tableProps?.onSortChanged("status", nextSort);
                                } else if (column.id === "permits_type_id") {
                                  nextSort = sortBy === "permits_type_id" && sortDirection === "desc" ? false : true;
                                  tableProps?.onSortChanged("permits_type_id", nextSort);
                                } else if (column.id === "assignee_ids") {
                                  nextSort = sortBy === "assignee_ids" && sortDirection === "desc" ? false : true;
                                  tableProps?.onSortChanged("assignee_ids", nextSort);
                                } else {
                                  nextSort = sortBy === column.id && sortDirection === "desc" ? false : true;
                                  tableProps?.onSortChanged(column.id, nextSort);
                                }
                              }}
                            >
                              {column.render("Header")}
                              {column.id === sortBy ||
                              (column.id === "created_at" && sortBy === "submitted_at") ||
                              (column.id === "expires_at" && sortBy === "expired_at") ||
                              (column.id === "creator_id" && sortBy === "creator.full_name.keyword") ||
                              (column.id === "balance_due" && sortBy === "total_pending") ||
                              (column.id === "dateEntered" && sortBy === "dateEntered") ||
                              (column.id === "dateLastUpdated" && sortBy === "dateLastUpdated") ||
                              (column.id === "dateDue" && sortBy === "dateDue") ||
                              (column.id === "dateExpectClose" && sortBy === "dateExpectClose") ||
                              (column.id === "confirmation_number" && sortBy === "confirmation_number.keyword") ||
                              (column.id === "reservation_number" && sortBy === "reservation_number.keyword") ||
                              sortBy === column.id ? (
                                sortDirection === "desc" ? (
                                  <Icon name='caret down' />
                                ) : (
                                  <Icon name='caret up' />
                                )
                              ) : (
                                ""
                              )}
                            </div>
                          }
                        />
                      );
                    }}
                    cellRenderer={({ columnIndex, rowData }) => rowData.cells[columnIndex].render("Cell")}
                  />
                ))}
              </Table>
            )}
          </AutoSizer>
        )}
      </InfiniteLoader>
    </div>
  );
};
